import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { PackagePlus, Loader2, MoveRightIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { usePartForm } from "@/hooks/usePartForm";
import VehicleInfoFields from "@/components/add-part/VehicleInfoFields";
import PartInfoFields from "@/components/add-part/PartInfoFields";
import PricingAndLocationFields from "@/components/add-part/PricingAndLocationFields";
import ImageUploadField from "@/components/add-part/ImageUploadField";
import PartAddedSuccess from "@/components/add-part/PartAddedSuccess";
import { Input } from "@/components/ui/input";
import FormField from "./add-part/FormField";
import PaymentGenerateModal from "./Modal/PaymentGenerateModal";
import { useSelector } from "react-redux";

const AddPartForm = ({ selectedPart }) => {
  const { currentSubscription } = useSelector((state) => state.auth);
  const [open, setOpen] = useState(false);
  const currentPlan = currentSubscription?.previousPlanId
    ? currentSubscription?.previousPlanId
    : currentSubscription?.planId;

  const {
    formRef,
    formData,
    errors,
    isSubmitting,
    images,
    imageCount,
    availableModels,
    availableParts,
    partAddedSuccessfully,
    addedPartName,
    isInitialized,
    years,
    handleInputChange,
    handleSelectChange,
    handleImageChange,
    removeImage,
    handleSubmit,
    resetFormForNewPart,
    navigate
  } = usePartForm(selectedPart);

  if (selectedPart && !isInitialized) {
    return (
      <motion.div
        className="flex flex-col items-center justify-center p-8 bg-card rounded-xl shadow-2xl border border-border/20 min-h-[400px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="min-h-[386px] w-full flex items-center justify-center">
          <CardContent className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Loading...
            </h2>
            <p className="text-muted-foreground">
              Please wait while we fetch your data...
            </p>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (partAddedSuccessfully) {
    return (
      <PartAddedSuccess
        addedPartName={addedPartName}
        onAddAnother={resetFormForNewPart}
        onGoToDashboard={() =>
          navigate("/dashboard", {
            state: { partAdded: true, timestamp: Date.now() }
          })
        }
        isSubmitting={isSubmitting}
      />
    );
  }

  return (
    <motion.form
      ref={formRef}
      onSubmit={handleSubmit}
      className="space-y-6 bg-card p-4 sm:p-6 md:p-8 rounded-xl shadow-2xl border border-border/20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <VehicleInfoFields
          formData={formData}
          handleSelectChange={handleSelectChange}
          handleInputChange={handleInputChange}
          errors={errors}
          availableModels={availableModels}
          isSubmitting={isSubmitting}
          years={years}
        />
        <PartInfoFields
          formData={formData}
          handleSelectChange={handleSelectChange}
          handleInputChange={handleInputChange}
          errors={errors}
          availableParts={availableParts}
          isSubmitting={isSubmitting}
        />
        <PricingAndLocationFields
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
          isSubmitting={isSubmitting}
        />
      </div>
      {currentPlan == "premium" && (
        <div className="justify-between grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <FormField
            id="stripePaymentLink"
            label="Your Stripe Payment Link"
            error={errors.stripePaymentLink}
            description="Used to help buyers pay for your product."
            required={false}
          >
            <Input
              id="stripePaymentLink"
              name="stripePaymentLink"
              placeholder="https://buy.stripe.com/test_dRm28*********"
              value={formData.stripePaymentLink}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
          </FormField>
          <p
            className="flex mt-2 justify-end items-center inline-center hover:underline cursor-pointer text-sm font-medium text-primary hover:text-accent transition-colors duration-200"
            onClick={() => setOpen(true)}
          >
            How to Generate a Stripe Payment Link
            <MoveRightIcon className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </p>
        </div>
      )}

      <ImageUploadField
        images={images}
        imageCount={imageCount}
        handleImageChange={handleImageChange}
        removeImage={removeImage}
        isSubmitting={isSubmitting}
      />

      <div className="flex flex-col sm:flex-row justify-center sm:justify-end gap-3 pt-4">
        <Button
          type="submit"
          size={`${window.innerWidth < 786 ? "sm" : "lg"}`}
          className="bg-gradient-to-r from-primary via-purple-600 to-accent text-primary-foreground hover:opacity-90 transition-opacity shadow-lg"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              {selectedPart ? "Updating Part..." : "Adding Part..."}
            </>
          ) : (
            <>
              <PackagePlus className="mr-2 h-5 w-5" />
              {selectedPart
                ? "Update Part in Inventory"
                : "Add Part to Inventory"}
            </>
          )}
        </Button>
      </div>
      <PaymentGenerateModal open={open} setOpen={setOpen} isBulk={false} />
    </motion.form>
  );
};

export default AddPartForm;
